import { AnalyticsEvent, EventCategory, trackUnifiedEvent } from '@/services/analytics'

/** The invoicing chat's actions that change something, or answer without the model. A fixed set, so the property stays bounded. */
type InvoicingAction = 'quick_answer' | 'confirm_pod_match' | 'create_invoice' | 'skip_and_create' | 'create_batch' | 'send_email' | 'prepare_factoring_batch'

/** How an action ended: done, refused by its card or route (in its own words), done for some of its items only, or an error. */
export type InvoicingOutcome = 'succeeded' | 'refused' | 'partial' | 'error'

/** Bounded properties only: no ids, load numbers, emails or free text. */
interface InvoicingActionProps {
    /** The number of loads or invoices the action covers. */
    count?: number
    /** A fixed variant of the action, e.g. a suggestion's kind or a batch's mode. */
    variant?: string
}

const fire = (action: AnalyticsEvent, props: Record<string, string | number>): void => {
    try {
        trackUnifiedEvent({ action, category: EventCategory.FEATURE_USE, ...props })
    } catch {
        /* analytics must never break the chat */
    }
}

/**
 * Runs one invoicing action with its events: `initiated` before, then `succeeded` or `failed` with the outcome `outcomeOf` reads from
 * the result (a refusal is an answer, not a throw). A throw is `failed` with `error`, and is thrown on to the caller unchanged.
 */
export async function trackedInvoicingAction<T>(
    action: InvoicingAction,
    run: () => Promise<T>,
    outcomeOf: (result: T) => InvoicingOutcome = () => 'succeeded',
    props: InvoicingActionProps = {}
): Promise<T> {
    const base = { invoicing_action: action, ...(props.count !== undefined && { count: props.count }), ...(props.variant && { variant: props.variant }) }
    fire(AnalyticsEvent.AGENT_INVOICING_ACTION_INITIATED, base)
    try {
        const result = await run()
        const outcome = outcomeOf(result)
        fire(outcome === 'succeeded' ? AnalyticsEvent.AGENT_INVOICING_ACTION_SUCCEEDED : AnalyticsEvent.AGENT_INVOICING_ACTION_FAILED, { ...base, outcome })
        return result
    } catch (error) {
        fire(AnalyticsEvent.AGENT_INVOICING_ACTION_FAILED, { ...base, outcome: 'error' })
        throw error
    }
}

/** The outcome of a run of several items (emails, batches): all done, some, or none. */
export const outcomeOfMany = (done: number, total: number): InvoicingOutcome => (done === total ? 'succeeded' : done === 0 ? 'refused' : 'partial')
